import React, { useState, useEffect } from "react";
import { CreateInvitationData } from "../api/invitations";
import { Invitation } from "../lib/supabase";

interface AddInvitationFormProps {
  onSubmit: (data: CreateInvitationData) => Promise<void>;
  onCancel: () => void;
  invitation?: Invitation; // Optional invitation for editing
}

interface FormField {
  key: keyof CreateInvitationData;
  label: string;
  type: "text" | "checkbox";
  required: boolean;
  placeholder?: string;
  maxLength?: number;
  conditional?: {
    dependsOn: keyof CreateInvitationData;
    condition: boolean;
  };
  transform?: (value: string) => string;
}

// Function to generate a random 5-character code with uppercase letters and numbers
const generateInvitationCode = (): string => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let result = "";
  for (let i = 0; i < 5; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

const formFields: FormField[] = [
  {
    key: "code",
    label: "Invitation Code",
    type: "text",
    required: true,
    placeholder: "Enter invitation code...",
    maxLength: 10,
    transform: (value: string) => value.toUpperCase(),
  },
  {
    key: "guest_names",
    label: "Guest Names",
    type: "text",
    required: true,
    placeholder: "Enter guest names...",
  },
  {
    key: "ceremony",
    label: "Invited to Ceremony",
    type: "checkbox",
    required: false,
  },
  {
    key: "luncheon",
    label: "Invited to Luncheon",
    type: "checkbox",
    required: false,
  },
  {
    key: "reception",
    label: "Invited to Reception",
    type: "checkbox",
    required: false,
  },
  {
    key: "plus_one",
    label: "Plus One Allowed",
    type: "checkbox",
    required: false,
  },
  {
    key: "family_allowed",
    label: "Family Allowed",
    type: "checkbox",
    required: false,
  },
  {
    key: "plus_one_name",
    label: "Plus One Name",
    type: "text",
    required: false,
    placeholder: "Enter plus one name...",
    conditional: {
      dependsOn: "plus_one",
      condition: true,
    },
  },
];

const AddInvitationForm: React.FC<AddInvitationFormProps> = ({
  onSubmit,
  onCancel,
  invitation,
}) => {
  const [formData, setFormData] = useState<CreateInvitationData>({
    code: "",
    guest_names: "",
    ceremony: false,
    luncheon: false,
    reception: false,
    plus_one: false,
    family_allowed: false,
    plus_one_name: "",
  });

  // Initialize form with invitation data if editing
  useEffect(() => {
    if (invitation) {
      setFormData({
        code: invitation.code,
        guest_names: invitation.guest_names,
        ceremony: invitation.ceremony,
        luncheon: invitation.luncheon,
        reception: invitation.reception,
        plus_one: invitation.plus_one,
        family_allowed: invitation.family_allowed,
        plus_one_name: invitation.plus_one_name || "",
      });
    } else {
      // Auto-generate code for new invitations
      setFormData({
        code: generateInvitationCode(),
        guest_names: "",
        ceremony: false,
        luncheon: false,
        reception: false,
        plus_one: false,
        family_allowed: false,
        plus_one_name: "",
      });
    }
  }, [invitation]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await onSubmit(formData);

      // Reset form with new generated code
      setFormData({
        code: generateInvitationCode(),
        guest_names: "",
        ceremony: false,
        luncheon: false,
        reception: false,
        plus_one: false,
        family_allowed: false,
        plus_one_name: "",
      });
    } catch (error) {
      console.error("Form submission error:", error);
    }
  };

  const handleInputChange =
    (field: keyof CreateInvitationData) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value =
        e.target.type === "checkbox" ? e.target.checked : e.target.value;
      const fieldConfig = formFields.find((f) => f.key === field);

      let finalValue = value;
      if (fieldConfig?.transform && typeof value === "string") {
        finalValue = fieldConfig.transform(value);
      }

      setFormData((prev) => ({ ...prev, [field]: finalValue }));
    };

  const renderField = (field: FormField) => {
    // Check conditional rendering
    if (field.conditional) {
      const shouldShow =
        formData[field.conditional.dependsOn] === field.conditional.condition;
      if (!shouldShow) return null;
    }

    const commonProps = {
      id: field.key,
      onChange: handleInputChange(field.key),
      required: field.required,
      className: "form-input",
    };

    if (field.type === "checkbox") {
      return (
        <div className="checkbox-group">
          <label className="checkbox-label">
            <input
              {...commonProps}
              type="checkbox"
              checked={formData[field.key] as boolean}
            />
            <span className="checkmark"></span>
            {field.label}
          </label>
        </div>
      );
    }

    return (
      <input
        {...commonProps}
        type="text"
        value={formData[field.key] as string}
        placeholder={field.placeholder}
        maxLength={field.maxLength}
      />
    );
  };

  const renderFieldGroup = (fields: FormField[]) => {
    const visibleFields = fields.filter((field) => {
      if (field.conditional) {
        return (
          formData[field.conditional.dependsOn] === field.conditional.condition
        );
      }
      return true;
    });

    if (visibleFields.length === 0) return null;

    if (visibleFields.length === 1) {
      const field = visibleFields[0];
      return (
        <div key={field.key} className="form-group">
          {field.type === "checkbox" ? (
            renderField(field)
          ) : (
            <>
              <label htmlFor={field.key}>{field.label}</label>
              {renderField(field)}
            </>
          )}
        </div>
      );
    }

    return (
      <div key={visibleFields.map((f) => f.key).join("-")} className="form-row">
        {visibleFields.map((field) => (
          <div key={field.key} className="form-group">
            {field.type === "checkbox" ? (
              renderField(field)
            ) : (
              <>
                <label htmlFor={field.key}>{field.label}</label>
                {renderField(field)}
              </>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Group fields for layout
  const fieldGroups = [
    [formFields[0], formFields[1]], // code, guest_names
    [formFields[2], formFields[3], formFields[4]], // ceremony, luncheon, reception
    [formFields[5], formFields[6]], // plus_one, plus_one_name (conditional)
  ];

  return (
    <form onSubmit={handleSubmit} className="add-item-form">
      <h3>{invitation ? "Edit Invitation" : "Add New Invitation"}</h3>

      {fieldGroups.map(renderFieldGroup)}

      <div className="form-actions">
        <button type="submit" className="submit-btn">
          {invitation ? "Update Invitation" : "Add Invitation"}
        </button>
        <button type="button" onClick={onCancel} className="cancel-btn">
          Cancel
        </button>
      </div>
    </form>
  );
};

export default AddInvitationForm;
